import { Component, OnInit, OnDestroy, EventEmitter, Output, Input } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ManageService } from '../../../services/manage.service';
import { MatDialog } from '@angular/material/dialog';
import { SimpleMsgPopupComponent } from '../../../popup-collection/simple-msg/simple-msg-popup.component';
import { finalize, takeUntil } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { AdminRelationship, ManagementLink } from '../../../models/csp.model';
import { ApiResponse } from '../../../models/api-response.model';
import { ConfirmARCreationPopup } from '../../../popup-collection/comfirm-ar-creation-popup/confirm-ar-creation-popup.component';
import { AuthService } from '../../../services/auth.service';
import { CurrentUserModel } from '../../../models/account-ledger.model';

@Component({
  selector: 'dd-csp-tenant-managements',
  templateUrl: './csp-tenant-management.component.html',
  styleUrls: ['./csp-tenant-management.component.css']
})
export class CSPTenantManagementComponent implements OnInit, OnDestroy {
  
  @Input()  loadingState = false;
  @Output() loadingStateChange = new EventEmitter<boolean>();
  @Output() headerLink = new EventEmitter<ManagementLink>();
  readonly ADMIN_RELATIONSHIPS = ADMIN_RELATIONSHIPS;
  readonly Object = Object;
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  private tenantId: string;

  public tenantManagementDataLoaded = false;
  public isAdminRelationshipsLoading = false;
  public isStaffAccount = false;
  public adminRelationships: AdminRelationship[];
  public managementLinks: ManagementLink[] = [];
  public tenantManagementFormGroup = this.formBuilder.group({
    days: [7],
    title: ['1']
  })

  constructor(
    private formBuilder: FormBuilder,
    private manageService: ManageService,
    private dialog: MatDialog,
    private activatedRoute: ActivatedRoute,
    private authService: AuthService
  ) {
  }

  ngOnInit() {
    this.activatedRoute.paramMap.pipe(takeUntil(this.ngUnsubscribe)).subscribe(param => {
      this.tenantId = param.get('tenantId');
    })

    this.authService.getLoginInfo().pipe(takeUntil(this.ngUnsubscribe)).subscribe((user: CurrentUserModel) => {
      this.isStaffAccount = user.isStaffAccount;
    });
  }

  public openTab() {
    if (!this.tenantManagementDataLoaded) {
      this.loadTenantManagement();
      this.loadAdminRelationship();
      this.tenantManagementDataLoaded = true
    }
  }

  public loadAdminRelationship() {
    this.isAdminRelationshipsLoading = true;
    this.manageService.GetCustomerAdminRelationShip(this.tenantId.toLowerCase())
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((adminRelationships) => {
        this.adminRelationships = adminRelationships.list;
        this.isAdminRelationshipsLoading = false;  
      }, (error: any) => {
        this.adminRelationships = [];
        this.isAdminRelationshipsLoading = false;  
        console.info(error);
      });
  }

  public loadTenantManagement() {
    this.loadingState = true;
    this.loadingStateChange.emit(this.loadingState);
    this.manageService.getTenantManagementLinks(this.tenantId)
      .pipe(
        finalize(() => {
          this.loadingState = false;
          this.loadingStateChange.emit(this.loadingState);
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe({
        next: (res: ManagementLink[]) => {
          if (res && res.length) {
            this.managementLinks = res.filter(link => link.name.trim() !== 'Azure Active Directory');
            const headerLink = res.filter(link => link.name.trim() === 'Azure Active Directory')[0];
            this.headerLink.emit(headerLink);
          }
        },
        error: (err) =>  console.error(err)
      });
  }

  public terminateRelation(relationId): void {
    this.isAdminRelationshipsLoading = true;
    this.manageService.terminateAdminRelationship(relationId)
      .pipe(
        finalize(() => {
          this.isAdminRelationshipsLoading = false;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe({
        next: () => this.loadAdminRelationship(),
        error: (err) =>  console.error(err)
      });
  }

  public createAdminRelationship(): void  {
    this.dialog.open(SimpleMsgPopupComponent, {
      width: '350px',
      data: {
        title: 'Create Admin Relationship',
        msg: 'Are you sure you want to create an admin relationship?',
        buttonName: 'Continue'
      }
    }).afterClosed().subscribe((decision) => {
      if (decision) {
        const days = this.tenantManagementFormGroup.get('days').value;
        const title =this.tenantManagementFormGroup.get('title').value;
        this.isAdminRelationshipsLoading = true;
        this.manageService.createAdminRelationship(this.tenantId, days, title)
          .subscribe((res: ApiResponse) => {
            if (res.status) {
              this.loadAdminRelationship();
              this.dialog.open(ConfirmARCreationPopup, {
                width: '800px',
                data: {
                  link: res.responseMessage
                }
              })
            } else {
              this.isAdminRelationshipsLoading = false;
            }
          });
      }
    });
  }

  public openLinkInNewTab(url: string): void {
    window.open(url, "_blank");
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}

export const ADMIN_RELATIONSHIPS = {
  'SUPPORT_ANALYST': {
    displayName: 'Support Analyst',
    description: 'Support Analyst has access to read Azure Active Directory, raise and managed Microsoft Premium Support tickets on behalf of your tenancy.',
    value: '1',
    roles: [
      { displayName: 'Directory Readers', link: 'https://docs.microsoft.com/en-us/azure/active-directory/roles/permissions-reference#directory-readers' },
      { displayName: 'Service Support Administrator', link: 'https://docs.microsoft.com/en-us/azure/active-directory/roles/permissions-reference#service-support-administrator' }
    ]
  },
  'SUPPORT_OPERATOR': {
    displayName: 'Support Operator',
    description: 'Support Operator has the same permissions as Support Analyst with the additional permissions to support basic user administration including license allocation.',
    value: '2',
    roles: [
      { displayName: 'Directory Readers', link: 'https://docs.microsoft.com/en-us/azure/active-directory/roles/permissions-reference#directory-readers' },
      { displayName: 'Service Support Administrator', link: 'https://docs.microsoft.com/en-us/azure/active-directory/roles/permissions-reference#service-support-administrator' },
      { displayName: 'Helpdesk Administrator', link: 'https://docs.microsoft.com/en-us/azure/active-directory/roles/permissions-reference#helpdesk-administrator' },
      { displayName: 'License Administrator', link: 'https://docs.microsoft.com/en-us/azure/active-directory/roles/permissions-reference#license-administrator' }
    ]
  },
  'SUPPORT_ADMIN': {
    displayName: 'Support Admin',
    description: 'Support Admin has the same permissions as Support Operator with the additional permissions to support full user administration.',
    value: '3',
    roles: [
      { displayName: 'Directory Readers', link: 'https://docs.microsoft.com/en-us/azure/active-directory/roles/permissions-reference#directory-readers' },
      { displayName: 'Service Support Administrator', link: 'https://docs.microsoft.com/en-us/azure/active-directory/roles/permissions-reference#service-support-administrator' },
      { displayName: 'Helpdesk Administrator', link: 'https://docs.microsoft.com/en-us/azure/active-directory/roles/permissions-reference#helpdesk-administrator' },
      { displayName: 'License Administrator', link: 'https://docs.microsoft.com/en-us/azure/active-directory/roles/permissions-reference#license-administrator' },
      { displayName: 'User Administrator', link: 'https://docs.microsoft.com/en-us/azure/active-directory/roles/permissions-reference#user-administrator' }
    ]
  },
  'TENANCY_TECHNICIAN': { 
    displayName: 'Tenancy Technician',
    description: 'Tenancy Technician has the same permissions as Support Admin as well the global reader permissions to read all tenancy data and administer billing data.',
    value: '4',
    roles: [
      { displayName: 'Directory Readers', link: 'https://docs.microsoft.com/en-us/azure/active-directory/roles/permissions-reference#directory-readers' },
      { displayName: 'Service Support Administrator', link: 'https://docs.microsoft.com/en-us/azure/active-directory/roles/permissions-reference#service-support-administrator' },
      { displayName: 'Helpdesk Administrator', link: 'https://docs.microsoft.com/en-us/azure/active-directory/roles/permissions-reference#helpdesk-administrator' },
      { displayName: 'License Administrator', link: 'https://docs.microsoft.com/en-us/azure/active-directory/roles/permissions-reference#license-administrator' },
      { displayName: 'User Administrator', link: 'https://docs.microsoft.com/en-us/azure/active-directory/roles/permissions-reference#user-administrator' },
      { displayName: 'Global Reader', link: 'https://docs.microsoft.com/en-us/azure/active-directory/roles/permissions-reference#global-reader' },
      { displayName: 'Attribute Definition Reader', link: 'https://docs.microsoft.com/en-us/azure/active-directory/roles/permissions-reference#attribute-definition-reader' },
      { displayName: 'Attribute Assignment Reader', link: 'https://docs.microsoft.com/en-us/azure/active-directory/roles/permissions-reference#attribute-assignment-reader' },
      { displayName: 'Message Center Reader', link: 'https://docs.microsoft.com/en-us/azure/active-directory/roles/permissions-reference#message-center-reader' },
      { displayName: 'Message Center Privacy Reader', link: 'https://docs.microsoft.com/en-us/azure/active-directory/roles/permissions-reference#message-center-privacy-reader' },
      { displayName: 'Security Reader ', link: 'https://docs.microsoft.com/en-us/azure/active-directory/roles/permissions-reference#security-reader' },
      { displayName: 'Reports Reader', link: 'https://docs.microsoft.com/en-us/azure/active-directory/roles/permissions-reference#reports-reader' },
      { displayName: 'Billing Administrator', link: 'https://docs.microsoft.com/en-us/azure/active-directory/roles/permissions-reference#billing-administrator' },
      { displayName: 'Compliance Administrator', link: 'https://docs.microsoft.com/en-us/azure/active-directory/roles/permissions-reference#compliance-administrator' },
      { displayName: 'Insights Administrator', link: 'https://docs.microsoft.com/en-us/azure/active-directory/roles/permissions-reference#insights-administrator' }
    ]
  }
};